import type { StatusTone } from '@vnetwork/ui/data';
import {
  PERMISSION_ACTIONS,
  type TeamRole,
  type MemberStatus,
  type PermissionAction,
} from '@/api/teams';

export { formatDate, formatDateTime } from '@/lib/format-date';

type BadgeTone = 'neutral' | 'primary' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

/** Badge tone for a user's role within a team. */
export const TEAM_ROLE_TONE: Record<TeamRole, BadgeTone> = {
  owner: 'primary',
  member: 'neutral',
};

export const TEAM_ROLE_LABEL: Record<TeamRole, string> = {
  owner: 'Owner',
  member: 'Member',
};

/** StatusPill tone for a member's lifecycle status. */
export const MEMBER_STATUS_TONE: Record<MemberStatus, StatusTone> = {
  active: 'success',
  pending: 'warning',
  inactive: 'muted',
};

export const MEMBER_STATUS_LABEL: Record<MemberStatus, string> = {
  active: 'Active',
  pending: 'Pending',
  inactive: 'Inactive',
};

/** Build the public invitation URL for a token (consumed by the auth flow). */
export function invitationUrl(token: string): string {
  return `${window.location.origin}/auth/invitation?token=${token}`;
}

// Management actions only — excludes the `all` pseudo-action and the exclusive
// `report` role (a mutually-exclusive read-only mode).
const MANAGEMENT_ACTIONS = PERMISSION_ACTIONS.filter((a) => a.key !== 'all' && a.key !== 'report').map((a) => a.key);

/**
 * Toggle one permission action, keeping `all` in sync and `report` mutually
 * exclusive with management actions:
 * - checking `report` clears everything else (the read-only role is exclusive);
 * - checking any management action (or `all`) clears `report`;
 * - checking `all` selects every management action; selecting the last individual
 *   action auto-adds `all`; unchecking any drops `all`.
 */
export function toggleAction(actions: string[], action: PermissionAction): string[] {
  if (action === 'report') {
    return actions.includes('report') ? [] : ['report'];
  }
  // Selecting any management action exits the exclusive read-only role.
  const base = actions.filter((a) => a !== 'report');
  if (action === 'all') {
    return base.includes('all') ? [] : [...MANAGEMENT_ACTIONS, 'all'];
  }
  if (base.includes(action)) {
    return base.filter((a) => a !== action && a !== 'all');
  }
  const next = [...base, action];
  if (MANAGEMENT_ACTIONS.every((k) => next.includes(k))) next.push('all');
  return next;
}

/** Human label for a permission action key (falls back to the raw key). */
export function actionLabel(key: string): string {
  return PERMISSION_ACTIONS.find((a) => a.key === key)?.label ?? key;
}
